// src/components/ProjetsGridNeon.tsx
import React, { useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    MobileStepper,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import LaunchIcon from '@mui/icons-material/Launch';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

// == IMPORTS D'IMAGES (Puissance 4) ==
import puissance4Titre from '../Images/puissance4Titre.jpg';
import puissance4_1 from '../Images/Puissance4_1.png';
import puissance4_2 from '../Images/Puissance4_2.png';
import puissance4_3 from '../Images/Puissance4_3.png';
import puissance4_4 from '../Images/Puissance4_4.png';
import puissance4_5 from '../Images/Puissance4_5.svg';

// == IMPORTS D'IMAGES (Sqluedo ) ==
import SqluedoTitre from '../Images/Sqluedo.png';
import Sqluedo1 from '../Images/Sqluedo1.png';
import Sqluedo2 from '../Images/Sqluedo2.png';
import Sqluedo3 from '../Images/Sqluedo3.png';

// == IMPORTS D'IMAGES (Hub Python) ==
import Pygame from '../Images/pygame.png';

// Interface d'un projet bilingue
interface Projet {
    title: { en: string; fr: string };
    shortDesc: { en: string; fr: string };
    date: { en: string; fr: string };
    buttonLabel: { en: string; fr: string };
    image: string;
    images: string[];
    technologies: string[];
}

// Données bilingues des projets
const projetsData: Projet[] = [
    {
        title: { en: 'Connect Four', fr: 'Puissance 4' },
        shortDesc: {
            en: 'Connect Four game in C# including a console version.',
            fr: 'Jeu de Puissance 4 en C# avec aussi une version console.',
        },
        date: {
            en: 'Established in 2024',
            fr: 'Etabli en 2024',
        },
        buttonLabel: {
            en: 'Learn more',
            fr: 'En savoir plus',
        },
        image: puissance4Titre,
        images: [
            puissance4_1,
            puissance4_2,
            puissance4_3,
            puissance4_4,
            puissance4_5,
        ],
        technologies: ['C#', 'MAUI', '.NET'],
    },
    {
        title: { en: 'Sqluedo', fr: 'Sqluedo' },
        shortDesc: {
            en: 'Sqluedo is a web and mobile game where you solve investigations using SQL query blocks.',
            fr: "SQLuedo est un jeu sur navigateur web et sur application mobile qui consiste à résoudre des enquêtes via des blocs de requête SQL.",
        },
        date: {
            en: 'Established in 2024',
            fr: 'Etabli en 2024',
        },
        buttonLabel: {
            en: 'Learn more',
            fr: 'En savoir plus',
        },
        image: SqluedoTitre,
        images: [Sqluedo1, Sqluedo2, Sqluedo3],
        technologies: ['PHP', 'HTML', 'CSS', 'Entity Framework', 'Android Studio'],
    },
    {
        title: { en: 'Python Hub', fr: 'Hub Python' },
        shortDesc: {
            en: 'A collection of mini-games developed with Pygame.',
            fr: 'Plusieurs mini jeux en pygame.',
        },
        date: {
            en: 'Established in 2024',
            fr: 'Etabli en 2024',
        },
        buttonLabel: {
            en: 'Learn more',
            fr: 'En savoir plus',
        },
        image: Pygame,
        images: [Pygame],
        technologies: ['Python'],
    },
];

// === STYLED COMPONENTS & RESPONSIVE STYLES ===

// Conteneur principal
const Container = styled(Box)(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : '#fff',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    borderRadius: theme.shape.borderRadius,
    maxWidth: 1200,
    margin: '0 auto',
}));

// Titre de section
const SectionTitle = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.8rem',
    },
}));

// Carte de projet
const ProjectCard = styled(Card)(({ theme }) => ({
    borderRadius: 8,
    boxShadow:
        theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(255,255,255,0.1)'
            : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'scale(1.02)',
    },
}));

// Conteneur des chips
const TechChipsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    margin: `${theme.spacing(2)} 0`,
}));

// Style commun pour les chips
const techChipStyle = {
    backgroundColor: '#f1f1f1',
    color: '#333',
    borderRadius: '9999px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    height: '28px',
};

// Bouton Learn More
const MoreButton = styled(Button)(({ theme }) => ({
    backgroundColor: '#FF6200',
    color: '#fff',
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
    '&:hover': {
        backgroundColor: '#e65a00',
    },
}));

interface ProjetsGridNeonProps {
    language: 'en' | 'fr';
}

export default function ProjetsGridNeon({ language }: ProjetsGridNeonProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [activeStep, setActiveStep] = useState<number>(0);
    const theme = useTheme();

    // Texte pour le titre de section
    const texts = {
        en: { sectionTitle: "Projects" },
        fr: { sectionTitle: "Projets" },
    };
    const { sectionTitle } = texts[language];

    // Ouvrir le dialog et réinitialiser le carrousel
    const handleOpen = (index: number) => {
        setOpenIndex(index);
        setActiveStep(0);
    };

    // Fermer le dialog
    const handleClose = () => {
        setOpenIndex(null);
    };

    // Navigation du carrousel
    const handleNext = () => {
        if (openIndex === null) return;
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    return (
        <Container>
            <SectionTitle variant="h4">{sectionTitle}</SectionTitle>
            <Grid container spacing={3}>
                {projetsData.map((projet, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <ProjectCard>
                            <CardMedia
                                component="img"
                                image={projet.image}
                                alt={projet.title[language]}
                                sx={{
                                    objectFit: 'contain',
                                    height: 180,
                                    width: '100%',
                                }}
                            />
                            <CardContent sx={{ textAlign: 'center' }}>
                                <TechChipsContainer>
                                    {projet.technologies.map((tech, i) => (
                                        <Chip key={i} label={tech} sx={techChipStyle} />
                                    ))}
                                </TechChipsContainer>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {projet.title[language]}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {projet.shortDesc[language]}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                                    {language === 'en' ? "Date:" : "Date :"} {projet.date[language]}
                                </Typography>
                                <MoreButton
                                    variant="contained"
                                    endIcon={<LaunchIcon />}
                                    onClick={() => handleOpen(index)}
                                >
                                    {projet.buttonLabel[language]}
                                </MoreButton>
                            </CardContent>
                        </ProjectCard>
                    </Grid>
                ))}
            </Grid>

            {/* Dialog pour le projet sélectionné */}
            <Dialog open={openIndex !== null} onClose={handleClose} maxWidth="md" fullWidth>
                {openIndex !== null && (
                    <>
                        <DialogTitle sx={{ m: 0, p: 2 }}>
                            {projetsData[openIndex].title[language]}
                            <IconButton
                                aria-label="close"
                                onClick={handleClose}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: theme.palette.grey[500],
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <img
                                    src={projetsData[openIndex].images[activeStep]}
                                    alt={`slide ${activeStep}`}
                                    style={{
                                        objectFit: 'contain',
                                        maxHeight: '500px',
                                        width: '100%',
                                        backgroundColor: '#222',
                                    }}
                                />
                            </Box>
                            <MobileStepper
                                variant="dots"
                                steps={projetsData[openIndex].images.length}
                                position="static"
                                activeStep={activeStep}
                                sx={{ maxWidth: 400, flexGrow: 1, mx: 'auto', mb: 2 }}
                                nextButton={
                                    <Button
                                        size="small"
                                        onClick={handleNext}
                                        disabled={activeStep === projetsData[openIndex].images.length - 1}
                                    >
                                        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                                    </Button>
                                }
                                backButton={
                                    <Button
                                        size="small"
                                        onClick={handleBack}
                                        disabled={activeStep === 0}
                                    >
                                        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                                    </Button>
                                }
                            />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                {projetsData[openIndex].shortDesc[language]}
                            </Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>
                                {language === 'en' ? "Date:" : "Date :"} {projetsData[openIndex].date[language]}
                            </Typography>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Container>
    );
}
